import React from 'react';
import ModalButton from 'components/ModalButton/ModalButton';
import autoRenewConfirmIcon from 'assets/images/icons/auto-renew-confirm.svg';
import autoRenewWarningIcon from 'assets/images/icons/auto-renew-warning.svg';
import CommonLargeModal from 'components/Modals/CommonLargeModal';

export interface AutoRenewInfoModalData {
  success: boolean;
  autoRenew: boolean;
  title: string;
  // CMS copy comes back from getMessage() as parsed elements, not a plain string.
  message: React.ReactNode;
}

interface AutoRenewInfoModalProps {
  show: boolean;
  data: AutoRenewInfoModalData;
  onConfirm: () => void;
  onExited: () => void;
  buttonText?: string | undefined;
}

interface AutoRenewMessageProps {
  title: string;
  message: React.ReactNode;
}

/**
 * Closes the opt-out journey. Everything the member needs to know about the
 * change itself - that it saved, and when their cover ends - is on the survey
 * that precedes this, so all that is left here is the sign-off. Opt-in still
 * takes its copy from the CMS via messages.successTitle/successMessage.
 */
const AutoRenewOptedOut = () => (
  <>
    <h4 className="mb-3 fw-bold fs-4" style={{ color: '#47474a' }}>Thank you</h4>
    <p className="fs-6 mb-4">Remember, if you change your mind you can turn on auto-renewal at any time.</p>
  </>
);

const AutoRenewMessage = ({ title, message }: AutoRenewMessageProps) => (
  <>
    <h4 className="mb-3 fw-bold fs-4" style={{ color: '#47474a' }}>{title}</h4>
    {/**
     * getMessage() hands us CMS copy already parsed into elements,
     * so the message often arrives wrapped in its own <p> (or several).
     */}
    <div className="fs-6 mb-4">{message}</div>
  </>
);

const AutoRenewInfoModal = ({ show, data, onConfirm, onExited, buttonText }: AutoRenewInfoModalProps) => (
  <CommonLargeModal
    show={show}
    onHide={onConfirm}
    onExited={onExited}
    title={data.success
      ? <img src={autoRenewConfirmIcon} alt="Auto renewal confirm icon" />
      : <img src={autoRenewWarningIcon} alt="Auto renewal warning icon" />}
  >
    {data.success && !data.autoRenew
      ? <AutoRenewOptedOut />
      : <AutoRenewMessage title={data.title} message={data.message} />}

    <ModalButton type="button" variant="filled" onClick={onConfirm}>
      {data.success ? 'Done' : (buttonText || 'Ok')}
    </ModalButton>
  </CommonLargeModal>
);

export default AutoRenewInfoModal;
